import { useEffect, useState } from 'react';
import { Caption, List, Placeholder, Section, Subheadline } from '@telegram-apps/telegram-ui';

import { getQuestionAnswer } from '../api/client.js';
import { getSentQuestions, type SentQuestion } from '../lib/sentQuestions.js';
import { useT } from '../hooks/useT.js';
import { useLocale } from '../i18n/LocaleContext.js';
import { useBackButton } from '../lib/nativeUi.js';
import styles from './MyQuestionsScreen.module.css';

/**
 * MyQuestionsScreen — «Мои вопросы»: список вопросов, которые ЭТО устройство
 * когда-либо задавало (retention-answer-loop). Продуктовое решение: спрашивающий
 * видит ответ СРАЗУ, как только автор ответит; идентичность — ТОЛЬКО локальная
 * (localStorage, `lib/sentQuestions.ts`), сервер её не хранит.
 *
 * Поток mount: читаем локальный журнал (newest first) → для каждого id
 * параллельно GET /questions/:id/answer (Promise.allSettled — отказ одного
 * запроса не валит остальные, CANON G3). Без поллинга — один проход на mount;
 * ручного «обновить» здесь не делаем (экран открывают нечасто, повторный вход
 * в вкладку уже перезапускает mount).
 *
 * work-038 §7.1: отказ запроса и честный `answered:false` РАЗВЕДЕНЫ по разным
 * состояниям. Раньше и то и другое схлопывалось в «ждём ответа» — экран врал,
 * утверждая «ответа ещё нет» там, где мы просто не смогли спросить.
 *
 * security/threat-models/answer-view.md: авторизация чтения = обладание
 * `questionId`, НЕ ownership — сервер не знает и не должен знать, что все эти
 * id принадлежат «одному и тому же» человеку.
 */
export interface MyQuestionsScreenProps {
  /** BackButton — возврат туда, откуда открыли экран (Settings/Inbox). */
  onBack: () => void;
}

type AnswerState =
  | { kind: 'loading' }
  | { kind: 'waiting' }
  /** Спросить сервер не удалось (сеть, 5xx, 401) — состояние ответа неизвестно. */
  | { kind: 'failed' }
  | { kind: 'answered'; text: string | null; cardImageUrl: string | null; answeredAt: string };

function formatDate(iso: string, locale: string): string {
  try {
    return new Date(iso).toLocaleDateString(locale === 'ru' ? 'ru-RU' : 'en-US', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
    });
  } catch {
    return iso;
  }
}

export function MyQuestionsScreen({ onBack }: MyQuestionsScreenProps) {
  const t = useT();
  const locale = useLocale();
  useBackButton(onBack);

  const [entries] = useState<SentQuestion[]>(() => getSentQuestions());
  const [answers, setAnswers] = useState<Record<string, AnswerState>>(() =>
    Object.fromEntries(entries.map((e) => [e.questionId, { kind: 'loading' } as AnswerState])),
  );

  useEffect(() => {
    if (entries.length === 0) return;
    let cancelled = false;
    void Promise.allSettled(
      entries.map((e) => getQuestionAnswer(e.questionId)),
    ).then((results) => {
      if (cancelled) return;
      setAnswers((prev) => {
        const next = { ...prev };
        results.forEach((res, i) => {
          const entry = entries[i];
          if (!entry) return;
          const id = entry.questionId;
          if (res.status === 'rejected') {
            // Запрос не дошёл (сеть, 5xx, 401) — мы НЕ знаем, ответили автор
            // или нет, и честно говорим об этом. Fail-soft (CANON G3) никуда
            // не делся: экран не падает, соседние карточки живут своей жизнью.
            // 401 отсюда дополнительно уедет в глобальный экран истёкшей
            // сессии (work-038 §4), но для НЕ-401 отказов честный текст здесь —
            // единственное, что видит человек.
            next[id] = { kind: 'failed' };
          } else if (res.value.answered && res.value.answer) {
            next[id] = {
              kind: 'answered',
              text: res.value.answer.text,
              cardImageUrl: res.value.answer.cardImageUrl,
              answeredAt: res.value.answer.createdAt,
            };
          } else {
            // Сервер честно ответил «ответа пока нет» (answered:false, либо
            // answered без тела ответа) — только это и есть настоящее ожидание.
            next[id] = { kind: 'waiting' };
          }
        });
        return next;
      });
    });
    return () => {
      cancelled = true;
    };
    // entries зафиксирован на mount (initial useState) — перезапуск не нужен.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (entries.length === 0) {
    return (
      <List>
        <Placeholder
          header={t.my_questions_empty_title}
          description={t.my_questions_empty_desc}
        />
      </List>
    );
  }

  return (
    <List>
      <Section header={t.my_questions_title}>
        {entries.map((entry) => {
          const state = answers[entry.questionId] ?? { kind: 'loading' as const };
          return (
            <div key={entry.questionId} className={styles.card}>
              {entry.label && (
                <Caption level="1" className={styles.label}>
                  {entry.label}
                </Caption>
              )}
              <Subheadline level="2" className={styles.date}>
                {formatDate(entry.createdAt, locale)}
              </Subheadline>

              {state.kind === 'loading' && (
                <Caption Component="p" className={styles.waiting}>
                  {t.my_questions_loading}
                </Caption>
              )}

              {state.kind === 'waiting' && (
                <Caption Component="p" className={styles.waiting}>
                  {t.my_questions_waiting}
                </Caption>
              )}

              {/* Тот же приглушённый слот, что и «ждём ответа» — новых видов не
                  заводим (work-038 §10 п.6), меняется только текст. */}
              {state.kind === 'failed' && (
                <Caption Component="p" className={styles.waiting}>
                  {t.my_questions_check_failed}
                </Caption>
              )}

              {state.kind === 'answered' && (
                <div className={styles.answerBlock}>
                  <Caption level="1" className={styles.answerLabel}>
                    {t.my_questions_answer_label}
                  </Caption>
                  {state.text !== null && (
                    <p className={styles.answerText}>{state.text}</p>
                  )}
                  {state.cardImageUrl !== null && (
                    <img
                      src={state.cardImageUrl}
                      alt={t.share_card_alt}
                      className={styles.cardImg}
                    />
                  )}
                </div>
              )}
            </div>
          );
        })}
      </Section>
    </List>
  );
}
